import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table, Space, PageHeader, Button, Select, Row, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reverse } from "named-urls";
import { sliceKey, reducer, actions } from "./slice";
import { packageListSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import { useAuth } from "../../../../contexts/auth";
import Text from "antd/lib/typography/Text";

const STORAGE_KEY = "packageListFilters";

const PackageList = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: packageListSaga });
  const dispatch = useDispatch();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;
  const isCompanyAdmin = auth.isCompanyAdmin;

  // Initialize state from sessionStorage
  const getInitialState = () => {
    const shouldUseCurrentCompany =
      isCompanyAdmin ||
      userRole === "regulatory_body_admin" ||
      userRole === "csc_admin";

    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        let parsedFilters = JSON.parse(saved);
        parsedFilters = {
          ...parsedFilters,
          selectedCompany: shouldUseCurrentCompany
            ? user.company._id
            : parsedFilters.selectedCompany,
        };

        return parsedFilters;
      }
    } catch (e) {
      console.error("Failed to parse saved filters:", e);
    }

    return {
      selectedCompany: shouldUseCurrentCompany ? user.company._id : null,
      filters: {
        commodityId: null,
      },
      page: 1,
      pageLimit: 10,
    };
  };

  const initialState = getInitialState();
  const [selectedCompany, setSelectedCompany] = useState(
    initialState.selectedCompany
  );
  const [filters, setFilters] = useState(initialState.filters);
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [pageLimit, setPageLimit] = useState(initialState.pageLimit);

  const packages = useSelector(selectors.selectPackages);
  const companies = useSelector(selectors.selectCompanies);
  const commodities = useSelector(selectors.selectCommodities);
  const loading = useSelector(selectors.selectLoading);
  const isCompaniesLoading = useSelector(selectors.selectCompaniesLoading);
  const isPackagesLoading = useSelector(selectors.selectPackagesLoading); 
  const totalPackages = useSelector(selectors.selectTotalPackages);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = {
      selectedCompany,
      filters,
      page: currentPage,
      pageLimit,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error("Failed to save filters:", e);
    }
  }, [selectedCompany, filters, currentPage, pageLimit]);

  useEffect(() => {
    if (!isCompanyAdmin) {
      dispatch(actions.fetchCompanies());
    }
    dispatch(actions.fetchCommodities());
  }, [dispatch, isCompanyAdmin]);

  // Fetch data
  useEffect(() => {
    if (selectedCompany) {
      dispatch(
        actions.fetchPackages({
          company: selectedCompany,
          page: currentPage,
          limit: pageLimit,
          filters,
        })
      );
    }
  }, [dispatch, selectedCompany, currentPage, pageLimit, filters]);

  const data = packages.map((item) => ({
    id: item._id,
    key: item._id,
    name: item.name,
    sampleVariant: item.sampleVariant,
    commodity: item?.tests[0]?.test?.commodity?.name,
    totalParameters: item?.tests?.length || 0,
    active: item.isActive ? "Active" : "Deactivated",
  }));

  const columns = [
    {
      title: "Package Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Sample Variant",
      dataIndex: "sampleVariant",
      key: "sampleVariant",
    },
    {
      title: "Commodity",
      dataIndex: "commodity",
      key: "commodityId",
      filterSearch: true,
      filters: commodities.map((commodity) => ({
        text: commodity.name,
        value: commodity._id,
      })),
      filteredValue: filters.commodityId || null,
    },
    {
      title: "Total Parameters",
      dataIndex: "totalParameters",
      key: "totalParameters",
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
    },
    {
      title: "Actions",
      key: "actions",
      render: (value, record) => (
        <Space size="middle">
          <Link
            key="view"
            to={reverse(`${routes.backOffice.packages.details.view}`, {
              id: record.id,
            })}
          >
            View
          </Link>
        </Space>
      ),
    },
  ];

  const handleTablePagination = (pagination, tableFilters) => {
    setCurrentPage(pagination.current);
    setPageLimit(pagination.pageSize);
    setFilters({
      commodityId: tableFilters.commodityId || null,
    });
  };

  const handleCompanyChange = (val) => {
    setSelectedCompany(val);
    setFilters({ commodityId: null });
    setCurrentPage(1);
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Packages"
        extra={
          userRole !== "agnext_reviewer" &&
          userRole !== "regulatory_body_admin" &&
          userRole !== "support_team" &&
          userRole !== "csc_admin"
            ? [
                <Button key="create" type="primary">
                  <Link to={routes.backOffice.packages.add}>New Package</Link>
                </Button>,
              ]
            : []
        }
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {!isCompanyAdmin &&
          userRole !== "regulatory_body_admin" &&
          userRole !== "csc_admin" && (
            <Row gutter={32} style={{ marginBottom: 30 }}>
              <Col span={8}>
                <strong>Select Company to fetch packages:</strong>
              </Col>
              <Col span={16}>
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  placeholder="Select a company"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={handleCompanyChange}
                  value={selectedCompany}
                  options={companies}
                  loading={isCompaniesLoading}
                />
              </Col>
            </Row>
          )}
        {selectedCompany && (
          <Table
            key={"id"}
            dataSource={data}
            columns={columns}
            loading={isPackagesLoading}
            pagination={{
              current: currentPage,
              pageSize: pageLimit,
              total: totalPackages,
              showSizeChanger: true,
              pageSizeOptions: ["10", "30", "50", "100"],
              showTotal: (total, range) => (
                <Text>{`${range[0]}-${range[1]} of ${total} items`}</Text>
              ),
            }}
            onChange={handleTablePagination}
          />
        )}
      </div>
    </div>
  );
};

export default PackageList;
